import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import { Table, Input, Typography, Spin, Select, message, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createIngredient,
  editIngredient,
  showIngredient,
  storeIngredient,
  updateIngredient,
} from '@app/api/feedExpIngredients.api';
import { ExpIngredientResponse, ExpIngredientLine } from '@app/models/feed_exp_ingredients/IngredientResponse';

interface FeedExpIngredientsProps {
  id?: string;
  type: 'edit' | 'create' | 'view';
  pageTitle: string;
  editable: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

import { useRef } from 'react';

const { Title } = Typography;
const { Option } = Select;

const ExpIngredientsComponent: React.FC<FeedExpIngredientsProps> = ({ type, pageTitle, editable, id }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [form] = Form.useForm();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<any[]>([]);
  const dataRef = useRef<any[]>([]);
  const [columnHeader, setColumnHeader] = useState('Raw Materials');
  const [existingLines, setExistingLines] = useState<ExpIngredientLine[]>([]);

  const years = Array.from({ length: 26 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const fetchFeeds = useCallback(async () => {
    setLoading(true);
    try {
      let response: ExpIngredientResponse | undefined;
      switch (type) {
        case 'create':
          response = await createIngredient();
          break;
        case 'edit':
        case 'view':
          if (id) {
            response = type === 'edit' ? await editIngredient(parseInt(id)) : await showIngredient(parseInt(id));
          }
          break;
      }

      if (response?.pr_feed_expected_ingredient?.pr_feed_expected_ingredient_lines) {
        setExistingLines(response.pr_feed_expected_ingredient.pr_feed_expected_ingredient_lines);
      }

      if (response?.pr_feed_types?.length) {
        setColumnHeader(response.pr_feed_types[0].type);

        const formattedData =
          response?.pr_feed_types?.[0]?.children?.map((category: { id: any; type: any; children: any[] }) => {
            const parent = {
              key: `parent-${category.id}`,
              category: category.type,
              id: category.id,
              isParent: true,
              children: [] as any[],
            };

            // Create children with mapped values from existing lines
            if (category.children?.length) {
              parent.children = category.children.map((child: any) => {
                // Find matching ingredient line if it exists
                const matchingLine = response?.pr_feed_expected_ingredient?.pr_feed_expected_ingredient_lines?.find(
                  (line: { pr_feed_type_id: any }) => line.pr_feed_type_id === child.id,
                );

                return {
                  key: `child-${child.id}`,
                  category: child.type,
                  id: child.id,
                  isSubcategory: true,
                  line_id: matchingLine?.id || null,
                  // Convert to string but handle decimal formatting
                  local_qty: matchingLine && matchingLine.local_qty ? String(parseFloat(matchingLine.local_qty)) : '',
                  imported_qty:
                    matchingLine && matchingLine.imported_qty ? String(parseFloat(matchingLine.imported_qty)) : '',
                };
              });
            }

            return parent;
          }) || [];

        setData(formattedData);
      }
    } catch (error) {
      console.error(`Error fetching Ingredients for ${type} mode:`, error);
    } finally {
      setLoading(false);
    }
  }, [type, id]);

  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds]);

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" onClick={handleSubmit} loading={loading} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/edit-exp-ingredient')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-exp-ingredient`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, loading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  const getTableData = () => {
    const tableData: any[] = [];

    data.forEach((parent) => {
      tableData.push({
        key: parent.key,
        category: parent.category,
        isParent: true,
      });

      if (parent.children && parent.children.length > 0) {
        parent.children.forEach((child: any) => {
          tableData.push({
            key: child.key,
            id: child.id,
            line_id: child.line_id,
            category: child.category,
            local_qty: child.local_qty,
            imported_qty: child.imported_qty,
            isSubcategory: true,
          });
        });
      }
    });

    if (searchTerm) {
      return tableData.filter((item) => item.category.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    return tableData;
  };

  const columns: ColumnsType<any> = [
    {
      title: columnHeader,
      dataIndex: 'category',
      key: 'category',
      render: (text, record) =>
        record.isParent ? (
          <strong style={{ fontSize: '16px' }}>{text}</strong>
        ) : record.isSubcategory ? (
          <span style={{ paddingLeft: '20px' }}>{text}</span>
        ) : null,
    },
    {
      title: 'Local Purchase(MT)',
      dataIndex: 'local_qty',
      key: 'local_qty',
      render: (text, record) =>
        record.isSubcategory && editable ? (
          <Input
            value={record.local_qty || ''}
            onChange={(e) => handleLocalPurchasedChange(record.key, e.target.value)}
            style={{ width: '100px' }}
          />
        ) : record.isSubcategory ? (
          <span>{record.local_qty || '0'}</span>
        ) : null,
    },
    {
      title: 'Imported (MT)',
      dataIndex: 'imported_qty',
      key: 'imported_qty',
      align: 'center',
      render: (text, record) =>
        record.isSubcategory && editable ? (
          <Input
            value={record.imported_qty || ''}
            onChange={(e) => handleImportedChange(record.key, e.target.value)}
            style={{ width: '100px' }}
          />
        ) : record.isSubcategory ? (
          <span>{record.imported_qty || '0'}</span>
        ) : null,
    },
  ];

  const handleLocalPurchasedChange = (key: string, value: string) => {
    setData((prevData) => {
      const newData = prevData.map((parent) => ({
        ...parent,
        children:
          parent.children?.map((child: any) => {
            if (child.key === key) {
              const updatedChild = { ...child, local_qty: value };
              return updatedChild;
            }
            return child;
          }) || [],
      }));

      return newData;
    });
  };

  const handleImportedChange = (key: string, value: string) => {
    setData((prevData) => {
      const newData = prevData.map((parent) => ({
        ...parent,
        children:
          parent.children?.map((child: any) => {
            if (child.key === key) {
              const updatedChild = { ...child, imported_qty: value };
              return updatedChild;
            }
            return child;
          }) || [],
      }));

      return newData;
    });
  };

  const handleSubmit = async () => {
    const ingredientLines: any[] = [];

    dataRef.current.forEach((parent, parentIndex) => {
      if (parent.children && parent.children.length > 0) {
        parent.children.forEach((child: any, childIndex: number) => {
          console.log(`Processing child ${childIndex}:`, {
            key: child.key,
            id: child.id,
            category: child.category,
            local_qty: child.local_qty,
            imported_qty: child.imported_qty,
            local_qty_type: typeof child.local_qty,
            imported_qty_type: typeof child.imported_qty,
          });

          // Simplified conversion - just parse the value directly
          const localQty = parseFloat(String(child.local_qty)) || 0;
          const importedQty = parseFloat(String(child.imported_qty)) || 0;

          console.log(`Final converted values for ${child.category}:`, {
            localQty,
            importedQty,
            original_local: child.local_qty,
            original_imported: child.imported_qty,
          });

          const lineData: {
            pr_feed_type_id: any;
            local_qty: number;
            imported_qty: number;
            id?: number;
          } = {
            pr_feed_type_id: child.id,
            local_qty: localQty,
            imported_qty: importedQty,
          };

          if (type === 'edit' && child.line_id) {
            lineData.id = child.line_id;
          }

          ingredientLines.push(lineData);
        });
      }
    });

    const formattedPayload = {
      year: selectedYear.toString(),
      pr_feed_expected_ingredient_lines: ingredientLines,
    };

    try {
      if (type === 'edit' && id) {
        await updateIngredient(parseInt(id), formattedPayload);
      } else {
        await storeIngredient(formattedPayload);
      }

      // message.success('Data submitted successfully!');
      navigate('/feed-exp-ingredients');
    } catch (error) {
      console.error('Error submitting data:', error);
      notification.error({ message: 'Failed to submit the data' });
    }
  };

  return (
    <BaseCard>
      <Title level={3}>{pageTitle || 'Feed Types'}</Title>
      <div style={{ display: 'flex', marginBottom: '16px', gap: '10px' }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder={`Search ${columnHeader}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <Select
          style={{ width: '120px' }}
          value={selectedYear}
          onChange={(value) => setSelectedYear(value)}
          disabled={type !== 'create'}
        >
          {years.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </div>
      {loading ? (
        <Spin size="small" />
      ) : (
        <Table
          columns={columns}
          dataSource={getTableData()}
          rowKey="key"
          pagination={false}
          bordered
          size="small"
          locale={{ emptyText: 'No data available' }}
        />
      )}
    </BaseCard>
  );
};

export default ExpIngredientsComponent;
